/// <reference lib="dom" />
import { getApiBaseUrl, SERVER_RESPONSE_CODES } from '@ptml/root-config';
import { QueryClient } from '@tanstack/react-query';
import type { CreateTRPCClientOptions, TRPCLink } from '@trpc/client';
import { httpLink } from '@trpc/react-query';
import superjson from 'superjson';

// Phase 1 mitigation: avoid coupling trpc-client to any specific app router (e.g. header-trpc)
// to prevent generated apps (banana) from inheriting an app:header dependency and failing
// module boundary lint. Use a generic TRPC router type placeholder. A future phase can
// introduce an abstract shared contract or per-app generic mapping.
// Using any here avoids generic constraint errors (InferrableClientTypes) while we keep
// the client decoupled from specific app routers. This still preserves runtime safety
// (no assumptions are made about procedure shapes). Future enhancement can replace with
// an abstracted interface or per-app mapping without reintroducing boundary coupling.
type GenericAppRouter = any;
// Removed node-fetch types as they are not needed in the browser environment

// DOM lib already provides RequestInit / RequestInfo globally; no need to alias.

const redirectToLoginOnAuthErrors = (httpStatus: number): void => {
    if (httpStatus !== SERVER_RESPONSE_CODES.UNAUTHORIZED) {
        return;
    }
    // In test (Vitest + Node) globalThis.location may be undefined while window.location is stubbed.
    const loc: { href: string } | undefined =
        (globalThis as unknown as { location?: { href: string } }).location ??
        (globalThis as unknown as { window?: { location?: { href: string } } }).window?.location;
    if (!loc) {
        // No location object available (e.g. server-side); nothing to redirect.
        return;
    }
    const current = loc.href;
    loc.href = `${getApiBaseUrl()}/fss/insurer/auth/login?returnUrl=${encodeURIComponent(current)}`;
};

export const trpcFetchPolyfill = async (
    input: globalThis.RequestInfo | globalThis.URL,
    init?: globalThis.RequestInit,
): Promise<globalThis.Response> => {
    if (typeof globalThis.fetch !== 'function') {
        throw new TypeError('Global fetch is not available in this environment');
    }
    const result = await globalThis.fetch(input as globalThis.RequestInfo, {
        ...init,
        credentials: 'include',
    });
    redirectToLoginOnAuthErrors(result.status);
    return result;
};

const ONE_DAY = 86_400_000; // 24h

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            gcTime: ONE_DAY,
            retryOnMount: false,
        },
        mutations: {
            retry: false,
        },
    },
});

export const generateTrpcClientOptions = (
    mfe: string,
): CreateTRPCClientOptions<GenericAppRouter> => {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/fss/insurer/bff/${mfe}`;

    const links: Array<TRPCLink<GenericAppRouter>> = [
        httpLink({
            url,
            fetch: trpcFetchPolyfill,
            headers: () => ({
                // Required header for the FSS
                'x-csrf': '1',
            }),
            transformer: superjson,
        }),
    ];

    return { links };
};
